import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Default color schemes
const DEFAULT_COLORS = {
  dark: {
    bgPrimary: '#212A31',
    bgSecondary: '#2E3944',
    bgTertiary: '#3a4a54',
    textPrimary: '#D3D9D4',
    textSecondary: '#748D92',
    textMuted: '#748D92',
    brand: '#76ABAE',
    brandLight: '#748D92',
    borderColor: 'rgba(116, 141, 146, 0.2)',
  },
  light: {
    bgPrimary: '#EDE8F5',
    bgSecondary: '#FFFFFF',
    bgTertiary: '#ADBBDA',
    textPrimary: '#2D3748',
    textSecondary: '#4F46E5',
    textMuted: '#8697C4',
    brand: '#4F46E5',
    brandLight: '#7091E6',
    borderColor: 'rgba(173, 187, 218, 0.5)',
  },
};

const STORAGE_KEY = 'propertyshop-custom-colors';
const DEFAULT_APPLIED_KEY = 'propertyshop-default-preset-applied';

const loadCustomColors = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const applyColorsToCSS = (colors, mode) => {
  const root = document.documentElement;
  if (!colors) return;

  root.style.setProperty('--bg-primary', colors.bgPrimary);
  root.style.setProperty('--bg-secondary', colors.bgSecondary);
  root.style.setProperty('--bg-tertiary', colors.bgTertiary);
  root.style.setProperty('--text-primary', colors.textPrimary);
  root.style.setProperty('--text-secondary', colors.textSecondary);
  root.style.setProperty('--text-muted', colors.textMuted);
  root.style.setProperty('--accent-gold', colors.brand);
  root.style.setProperty('--accent-gold-light', colors.brandLight);
  root.style.setProperty('--brand', colors.brand);
  root.style.setProperty('--brand-light', colors.brandLight);
  root.style.setProperty('--border-color', colors.borderColor);
  root.style.setProperty('--card-bg', mode === 'dark'
    ? `rgba(${hexToRgb(colors.bgSecondary)}, 0.85)`
    : `rgba(255, 255, 255, 0.95)`
  );
};

const clearInlineColors = () => {
  const root = document.documentElement;
  const props = ['--bg-primary','--bg-secondary','--bg-tertiary','--text-primary','--text-secondary','--text-muted','--accent-gold','--accent-gold-light','--brand','--brand-light','--border-color','--card-bg'];
  props.forEach(p => root.style.removeProperty(p));
};

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('propertyshop-theme');
    if (savedTheme) return savedTheme;
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
    return 'dark';
  });

  const [customColors, setCustomColors] = useState(() => loadCustomColors());

  // Apply default preset for new users (only once)
  useEffect(() => {
    const hasApplied = localStorage.getItem(DEFAULT_APPLIED_KEY);
    const hasCustom = localStorage.getItem(STORAGE_KEY);
    if (!hasApplied && !hasCustom) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/theme-presets/default`)
        .then(r => r.json())
        .then(data => {
          if (data.has_default && data.preset) {
            const preset = data.preset;
            const newColors = { dark: preset.dark, light: preset.light };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newColors));
            localStorage.setItem(DEFAULT_APPLIED_KEY, 'true');
            setCustomColors(newColors);
          } else {
            localStorage.setItem(DEFAULT_APPLIED_KEY, 'true');
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('propertyshop-theme', theme);
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    } else {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    }

    // Apply custom colors for current theme, or clear overrides
    const colors = customColors?.[theme];
    if (colors) {
      applyColorsToCSS(colors, theme);
    } else {
      clearInlineColors();
    }
  }, [theme, customColors]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const updateCustomColors = useCallback((mode, colors) => {
    setCustomColors(prev => {
      const next = { ...prev, [mode]: colors };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const resetColors = useCallback((mode) => {
    setCustomColors(prev => {
      const next = { ...prev };
      delete next[mode];
      if (!next.dark && !next.light) {
        localStorage.removeItem(STORAGE_KEY);
        clearInlineColors();
        return null;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      clearInlineColors();
      return next;
    });
  }, []);

  const getColors = useCallback((mode) => {
    return customColors?.[mode] || DEFAULT_COLORS[mode];
  }, [customColors]);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    customColors,
    updateCustomColors,
    resetColors,
    getColors,
    DEFAULT_COLORS,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
